import React, { useState } from 'react';
import { clsx, useSnackbar } from '@components/Provider';
import { Button, Drawer, Popover } from '@material-ui/core';
import useDrawer from '../useDrawer';
import useStyles from '../useStyles';
import {
  CapaErrorCode,
  CapaQuotationChatRoomStatus,
  FetchQuotationUserRfqDetailDocument,
  useQuitQuotationChatRoomMutation
} from '@generated/graphql';
import { useSelector } from '@store';
import MenuDrawerHeaderBar from './MenuDrawerHeaderBar';
import RfqPaymentInProgressNotiModal from './RfqPaymentInProgressNotiModal';

interface Props {
  matches: boolean;
}

const MenuDrawer: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  children,
  matches
}) => {
  const classes = useStyles();
  const rfqId = useSelector((state) => state.chat.rfqId);
  const chatRoomId = useSelector((state) => state.chat.chatRoomId);
  const chatRoomStatus = useSelector((state) => state.chat.chatRoomStatus);
  const isQuit = chatRoomStatus
    ? [
        CapaQuotationChatRoomStatus.ClosedClient,
        CapaQuotationChatRoomStatus.ClosedPartner
      ].includes(chatRoomStatus)
    : false;

  const [openModal, setOpenModal] = useState<boolean>(false);

  const [drawer, updateDrawer] = useDrawer();
  const isKebab = drawer.menu === 'kebab';
  const kebabButtonId = document.getElementById('kebab-button');

  const invisibleHeader = [
    'viewer',
    'purchaseorder',
    'quotation',
    'create_purchaseorder'
  ];

  const [quitChatRoom] = useQuitQuotationChatRoomMutation();

  const { enqueueSnackbar } = useSnackbar();

  const onQuit = async () => {
    if (!chatRoomId || !rfqId) return;
    const { data } = await quitChatRoom({
      variables: { chatRoomId: chatRoomId ?? '' },
      refetchQueries: [
        {
          query: FetchQuotationUserRfqDetailDocument,
          variables: { rfqId: rfqId }
        }
      ],
      awaitRefetchQueries: true
    });

    if (data) {
      const result = data.quitQuotationChatRoom.result;

      if (result === CapaErrorCode.Success) {
        return enqueueSnackbar('상담이 종료 되었습니다.', {
          variant: 'success'
        });
      } else if (result === CapaErrorCode.RfqPaymentInProgress) {
        updateDrawer('init');
        setOpenModal(true);
      } else {
        return enqueueSnackbar('잠시 후 다시 해주세요.', {
          variant: 'success'
        });
      }
    } else {
      return enqueueSnackbar('잠시 후 다시 해주세요.', {
        variant: 'success'
      });
    }
  };

  return (
    <>
      <RfqPaymentInProgressNotiModal
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
      {!matches && isKebab ? (
        <Popover
          classes={{ root: classes['Chat-Popover-root'] }}
          disablePortal
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          disableRestoreFocus
          anchorEl={kebabButtonId}
          open={isKebab && kebabButtonId ? true : false}
          onClose={() => {
            updateDrawer('init');
          }}
        >
          <Button
            disabled={isQuit}
            fullWidth
            className={clsx('consulting-close-btn', {
              closed: isQuit
            })}
            onClick={onQuit}
          >
            상담 종료하기
          </Button>
        </Popover>
      ) : (
        <Drawer
          className={classes['Chat-Drawer-root']}
          classes={{
            paper: clsx('drawer-paper', {
              kebab: matches && isKebab
            })
          }}
          PaperProps={{
            id: `drawer-content-${
              drawer.menu?.includes('purchaseorder')
                ? 'purchaseorder'
                : drawer.menu
            }`
          }}
          variant={matches && isKebab ? 'temporary' : 'persistent'}
          anchor={matches && isKebab ? 'bottom' : 'right'}
          open={drawer.menu ? true : false}
          transitionDuration={200}
          onClose={() => {
            if (drawer.menu === 'kebab') {
              updateDrawer('init');
            }
          }}
        >
          {!isKebab && (
            <>
              {!invisibleHeader.includes(drawer.menu) && (
                <MenuDrawerHeaderBar />
              )}

              <div
                className={clsx('drawer-content', {
                  purchaseorder: drawer.menu?.includes('purchaseorder'),
                  viewer: drawer.menu === 'viewer',
                  dm: drawer.menu === 'drawingfile_management',
                  request: drawer.menu === 'request',
                  quotation: drawer.menu === 'quotation'
                })}
              >
                {children}
              </div>
            </>
          )}

          {matches && isKebab && (
            <div className="drawer-content kebab-menu">
              <Button
                disabled={isQuit}
                fullWidth
                className={clsx('consulting-close-btn', {
                  closed: isQuit
                })}
                onClick={onQuit}
              >
                상담 종료하기
              </Button>
            </div>
          )}
        </Drawer>
      )}
    </>
  );
};

export default MenuDrawer;
